import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, translations, TranslationKey } from '../lib/translations';
import { api } from '../lib/api';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to get from localStorage first
    const saved = localStorage.getItem('preferred_language');
    if (saved === 'es' || saved === 'en') return saved;
    return 'en';
  });

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('preferred_language', lang);

    // Try to update on server if logged in (this also regenerates the phrase)
    try {
      const result = await api.updateLanguage(lang);
      // If a new phrase was generated, trigger a page refresh to show it
      if (result.newPhrase) {
        window.dispatchEvent(new CustomEvent('phrase-updated', { detail: result.newPhrase }));
      }
    } catch (e) {
      // Ignore if not logged in
    }
  };

  const t = (key: TranslationKey): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  // Sync with server language preference when auth changes
  useEffect(() => {
    const syncLanguage = async () => {
      try {
        const data = await api.getMe();
        if (data.member?.preferredLanguage) {
          const serverLang = data.member.preferredLanguage as Language;
          if (serverLang !== language) {
            setLanguageState(serverLang);
            localStorage.setItem('preferred_language', serverLang);
          }
        }
      } catch (e) {
        // Not logged in, use localStorage
      }
    };

    if (api.getToken()) {
      syncLanguage();
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
